import React, { useState, useEffect } from 'react';
import { Button, Tag, Spin, Empty, Tooltip } from 'antd';
import {
  PlayCircleOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DownOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faRobot,
  faUser,
  faDollarSign,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import VoiceCallService from '../../../services/VoiceCallService';
import {
  DetailsDrawer,
  DrawerHeader,
  DrawerSection,
  InfoGrid,
  TranscriptContainer,
  TranscriptEntry,
  StatusBadge
} from '../styled';

// Format duration
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format phone number
const formatPhoneNumber = (phone) => {
  if (!phone) return 'Unknown';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
};

// Status labels
const STATUS_LABELS = {
  'completed': 'Answered',
  'in-progress': 'In Call',
  'ringing': 'Ringing',
  'initiated': 'Connecting',
  'no-answer': 'No Answer',
  'busy': 'Busy',
  'failed': 'Failed',
  'canceled': 'Canceled'
};

// Outcome labels
const OUTCOME_LABELS = {
  'successful': 'Successful',
  'verification-failed': 'Verification Failed',
  'patient-unavailable': 'Patient Unavailable',
  'patient-declined': 'Patient Declined',
  'technical-error': 'Technical Error',
  'partial-completion': 'Partial Completion',
  'wrong-number': 'Wrong Number',
  'callback-requested': 'Callback Requested'
};

const CallDetailsDrawer = ({ open, call, onClose, onPlayRecording }) => {
  const [transcript, setTranscript] = useState([]);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  // Fetch transcript when drawer opens
  useEffect(() => {
    if (open && call?._id) {
      fetchTranscript();
    }
  }, [open, call]);

  const fetchTranscript = async () => {
    try {
      setLoadingTranscript(true);
      const response = await VoiceCallService.getCallTranscript(call._id);
      if (response.success) {
        setTranscript(response.transcript || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching transcript:', error);
      // Use outcome.aiAnalysis if available
      if (call?.outcome?.aiAnalysis?.keyPoints) {
        setTranscript([]);
      }
    } finally {
      setLoadingTranscript(false);
    }
  };

  const toggleSection = (key) => {
    setCollapsedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!call) return null;

  const hasRecording = call.recording?.s3Key || call.recording?.url;
  const totalCost = (call.cost?.twilioCharges || 0) + (call.cost?.openaiCharges || 0);

  return (
    <DetailsDrawer
      title={null}
      placement="right"
      width={480}
      open={open}
      onClose={onClose}
      closable
    >
      {/* Header */}
      <DrawerHeader $direction={call.direction}>
        <div className="header-icon">
          <FontAwesomeIcon icon={call.direction === 'inbound' ? faArrowDown : faArrowUp} />
        </div>
        <div className="header-content">
          <div className="contact-name">
            {call.patientName || 'Unknown Caller'}
          </div>
          <div className="phone-number">
            {formatPhoneNumber(call.phoneNumber)}
          </div>
        </div>
        <div className="header-stats">
          <div className="stat">
            <div className="stat-value">{formatDuration(call.duration)}</div>
            <div className="stat-label">Duration</div>
          </div>
          <div className="stat">
            <StatusBadge $status={call.status}>
              {STATUS_LABELS[call.status] || call.status}
            </StatusBadge>
          </div>
        </div>
      </DrawerHeader>

      {/* Play Recording Button */}
      {hasRecording && (
        <div style={{ padding: '16px 12px' }}>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            size="large"
            block
            onClick={onPlayRecording}
            style={{ borderRadius: 12, height: 48 }}
          >
            Play Recording
          </Button>
        </div>
      )}

      {/* Call Information */}
      <DrawerSection
        $collapsible
        $collapsed={collapsedSections.info}
        onClick={() => toggleSection('info')}
      >
        <div className="section-header">
          <span>
            <InfoCircleOutlined className="section-icon" />
            Call Information
          </span>
          <DownOutlined className="collapse-icon" />
        </div>
        <div className="section-content" onClick={e => e.stopPropagation()}>
          <InfoGrid>
            <div className="info-item">
              <div className="info-label">Direction</div>
              <div className="info-value">
                <FontAwesomeIcon 
                  icon={call.direction === 'inbound' ? faArrowDown : faArrowUp} 
                  style={{ marginRight: 6, color: call.direction === 'inbound' ? '#52c41a' : '#1890ff' }}
                />
                {call.direction === 'inbound' ? 'Inbound' : 'Outbound'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Status</div>
              <div className="info-value">
                <StatusBadge $status={call.status}>
                  {STATUS_LABELS[call.status] || call.status}
                </StatusBadge>
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">From Number</div>
              <div className="info-value">{formatPhoneNumber(call.fromNumber || '(864) 385-1918')}</div>
            </div>
            <div className="info-item">
              <div className="info-label">To Number</div>
              <div className="info-value">{formatPhoneNumber(call.phoneNumber)}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Started</div>
              <div className="info-value">
                {moment(call.initiatedAt).format('MMM D, YYYY h:mm A')}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Ended</div>
              <div className="info-value">
                {call.endedAt ? moment(call.endedAt).format('MMM D, YYYY h:mm A') : '-'}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Call ID</div>
              <div className="info-value" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {call.callSid || call._id}
              </div>
            </div>
            <div className="info-item">
              <div className="info-label">Duration</div>
              <div className="info-value">{formatDuration(call.duration)}</div>
            </div>
          </InfoGrid>
        </div>
      </DrawerSection>

      {/* Outcome & AI Analysis */}
      {call.outcome && (
        <DrawerSection
          $collapsible
          $collapsed={collapsedSections.outcome}
          onClick={() => toggleSection('outcome')}
        >
          <div className="section-header">
            <span>
              <RobotOutlined className="section-icon" />
              AI Agent Outcome
            </span>
            <DownOutlined className="collapse-icon" />
          </div>
          <div className="section-content" onClick={e => e.stopPropagation()}>
            <InfoGrid>
              <div className="info-item">
                <div className="info-label">Result</div>
                <div className="info-value">
                  <Tag color={call.outcome.result === 'successful' ? 'green' : 'orange'}>
                    {OUTCOME_LABELS[call.outcome.result] || call.outcome.result}
                  </Tag>
                </div>
              </div>
              <div className="info-item">
                <div className="info-label">Identity Verified</div>
                <div className="info-value">
                  {call.outcome.identityVerified ? (
                    <Tag color="green"><CheckCircleOutlined /> Yes</Tag>
                  ) : (
                    <Tag color="red"><CloseCircleOutlined /> No</Tag>
                  )}
                </div>
              </div>
              {call.outcome.aiAnalysis?.patientSentiment && (
                <div className="info-item">
                  <div className="info-label">Patient Sentiment</div>
                  <div className="info-value">
                    <Tag color={
                      call.outcome.aiAnalysis.patientSentiment === 'positive' ? 'green' :
                      call.outcome.aiAnalysis.patientSentiment === 'negative' ? 'red' : 'blue'
                    }>
                      {call.outcome.aiAnalysis.patientSentiment}
                    </Tag>
                  </div>
                </div>
              )}
              {call.outcome.aiAnalysis?.callQuality && (
                <div className="info-item">
                  <div className="info-label">Call Quality</div>
                  <div className="info-value">
                    <Tag color={
                      call.outcome.aiAnalysis.callQuality === 'excellent' ? 'green' :
                      call.outcome.aiAnalysis.callQuality === 'good' ? 'blue' : 'orange'
                    }>
                      {call.outcome.aiAnalysis.callQuality}
                    </Tag>
                  </div>
                </div>
              )}
            </InfoGrid>
            
            {call.outcome.summary && (
              <div style={{ marginTop: 16 }}>
                <div className="info-label" style={{ marginBottom: 8 }}>Summary</div>
                <div style={{ 
                  background: '#fafafa', 
                  padding: 12, 
                  borderRadius: 8,
                  fontSize: 14,
                  lineHeight: 1.6
                }}>
                  {call.outcome.summary}
                </div>
              </div>
            )}
            
            {call.outcome.aiAnalysis?.keyPoints?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="info-label" style={{ marginBottom: 8 }}>Key Points</div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {call.outcome.aiAnalysis.keyPoints.map((point, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DrawerSection>
      )}

      {/* Costs */}
      {totalCost > 0 && (
        <DrawerSection
          $collapsible
          $collapsed={collapsedSections.costs}
          onClick={() => toggleSection('costs')}
        >
          <div className="section-header">
            <span>
              <FontAwesomeIcon icon={faDollarSign} className="section-icon" />
              Cost Breakdown
            </span>
            <DownOutlined className="collapse-icon" />
          </div>
          <div className="section-content" onClick={e => e.stopPropagation()}>
            <InfoGrid>
              <div className="info-item">
                <div className="info-label">Twilio Charges</div>
                <div className="info-value">${(call.cost?.twilioCharges || 0).toFixed(4)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">OpenAI Charges</div>
                <div className="info-value">${(call.cost?.openaiCharges || 0).toFixed(4)}</div>
              </div>
              <div className="info-item">
                <div className="info-label">Total Cost</div>
                <div className="info-value" style={{ fontWeight: 600 }}>
                  ${totalCost.toFixed(4)}
                </div>
              </div>
            </InfoGrid>
          </div>
        </DrawerSection>
      )}

      {/* Transcript */}
      <DrawerSection
        $collapsible
        $collapsed={collapsedSections.transcript}
        onClick={() => toggleSection('transcript')}
      >
        <div className="section-header">
          <span>
            <FontAwesomeIcon icon={faComments} className="section-icon" />
            Conversation Transcript
          </span>
          <DownOutlined className="collapse-icon" />
        </div>
        <div className="section-content" onClick={e => e.stopPropagation()}>
          {loadingTranscript ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin />
            </div>
          ) : transcript.length > 0 ? (
            <TranscriptContainer>
              {transcript.map((entry, index) => (
                <TranscriptEntry key={index} $speaker={entry.speaker}>
                  <div className="speaker-avatar">
                    <FontAwesomeIcon icon={entry.speaker === 'agent' ? faRobot : faUser} />
                  </div>
                  <div className="transcript-content">
                    <div className="speaker-name">
                      {entry.speaker === 'agent' ? 'AI Agent' : 'Patient'}
                    </div>
                    <div className="transcript-text">{entry.text}</div>
                  </div>
                  {entry.timestamp && (
                    <Tooltip title="Jump to this moment">
                      <div className="transcript-time">
                        {formatDuration(entry.timestamp)}
                      </div>
                    </Tooltip>
                  )}
                </TranscriptEntry>
              ))}
            </TranscriptContainer>
          ) : (
            <div className="transcript-empty">
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No transcript available"
              />
            </div>
          )}
        </div>
      </DrawerSection>
    </DetailsDrawer>
  );
};

export default CallDetailsDrawer;
